import React from 'react';
import { marked } from 'marked';
import { memo, useId, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Utility function for className concatenation
const cn = (...classes: (string | undefined | null | boolean)[]) => {
  return classes.filter(Boolean).join(' ');
};

// Code Block Component (simplified version)
const CodeBlock = ({ language, children }: { language: string; children: string }) => {
  const displayLanguage = language === 'plaintext' ? 'code' : language;
  
  return (
    <div className="relative my-3 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
      {language !== 'plaintext' && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
          <span className="text-xs font-medium text-black dark:text-black-400 uppercase">
            {displayLanguage}
          </span>
        </div>
      )}
      <div className="overflow-x-auto">
        <pre className="p-3 m-0 text-sm leading-relaxed">
          <code className="text-black-800 dark:text-black font-mono whitespace-pre">
            {children}
          </code>
        </pre>
      </div>
    </div>
  );
};

// Link Component (simplified version)
const LinkComponent = ({ href, children, ...props }: any) => {
  if (!href) return <span {...props}>{children}</span>;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-500 hover:text-blue-600 underline"
      {...props}
    >
      {children}
    </a>
  );
};

export type MarkdownProps = {
  children: string;
  id?: string;
  className?: string;
  components?: Partial<Components>;
};

function parseMarkdownIntoBlocks(markdown: unknown): string[] {
  // Ensure input is string
  const src = typeof markdown === 'string' ? markdown : String(markdown || '');
  
  // Handle empty content
  if (!src.trim()) {
    return [src];
  }
  
  try {
    const tokens = marked.lexer(src);
    return tokens.map((token: any) => token.raw || '');
  } catch (error) {
    console.warn('Failed to parse markdown:', error);
    // Fallback to treating as single block
    return [src];
  }
}

function extractLanguage(className?: string): string {
  if (!className) return 'plaintext';
  const match = className.match(/language-(\w+)/);
  return match ? match[1] : 'plaintext';
}

const INITIAL_COMPONENTS: Partial<Components> = {
  code: function CodeComponent({ className, children, ...props }: any) {
    const isInline =
      !props.node?.position?.start.line ||
      props.node?.position?.start.line === props.node?.position?.end.line;

    if (isInline) {
      return (
        <span
          className={cn(
            'bg-gray-200 dark:bg-gray-700 rounded px-1 py-0.5 font-mono text-sm text-black dark:text-black-200',
            className
          )}
          {...props}
        >
          {children}
        </span>
      );
    }

    // For block code, we'll let the pre element handle it
    const language = extractLanguage(className);
    return (
      <code className={cn('font-mono text-sm', className)} {...props}>
        {children}
      </code>
    );
  },
  pre: function PreComponent({ children, ...props }: any) {
    // Extract language and code content
    const codeElement = children as any;
    const language = codeElement?.props?.className ? extractLanguage(codeElement.props.className) : 'plaintext';
    const codeContent = codeElement?.props?.children || children;

    // Get clean content without extra whitespace
    const cleanContent = typeof codeContent === 'string' 
      ? codeContent.trim() 
      : String(codeContent).trim();

    return (
      <CodeBlock language={language}>
        {cleanContent}
      </CodeBlock>
    );
  },
  a: LinkComponent,
  hr: function HRComponent(props: any) {
    return <hr className='my-6 border-t border-gray-200 dark:border-gray-600' {...props} />;
  },
  // Typography components
  h1: function H1Component({ children, ...props }: any) {
    return (
      <h1 className='text-2xl font-bold mb-4 text-black-900 dark:text-black' {...props}>
        {children}
      </h1>
    );
  },
  h2: function H2Component({ children, ...props }: any) {
    return (
      <h2 className='text-xl font-bold mb-3 text-b dark:text-black' {...props}>
        {children}
      </h2>
    );
  },
  h3: function H3Component({ children, ...props }: any) {
    return (
      <h3 className='text-lg font-semibold mb-2 text-black dark:text-black' {...props}>
        {children}
      </h3>
    );
  },
  p: function PComponent({ children, ...props }: any) {
    return (
      <p className='mb-2 text-black dark:text-black leading-relaxed last:mb-0' {...props}>
        {children}
      </p>
    );
  },
  // List components
  ul: function ULComponent({ children, ...props }: any) {
    return (
      <ul className='mb-3 pl-5 list-disc text-black dark:text-black space-y-1' {...props}>
        {children}
      </ul>
    );
  },
  ol: function OLComponent({ children, ...props }: any) {
    return (
      <ol className='mb-3 pl-5 list-decimal text-black dark:text-black space-y-1' {...props}>
        {children}
      </ol>
    );
  },
  li: function LIComponent({ children, ...props }: any) {
    return (
      <li className='break-words leading-relaxed' {...props}>
        {children}
      </li>
    );
  },
  // Table components
  table: function TableComponent({ children, ...props }: any) {
    return (
      <div className='overflow-x-auto my-4 w-full'>
        <table className='w-full table-auto border-collapse text-sm border border-gray-200 dark:border-gray-700' {...props}>
          {children}
        </table>
      </div>
    );
  },
  thead: function TheadComponent({ children, ...props }: any) {
    return (
      <thead className='bg-gray-100 dark:bg-gray-800' {...props}>
        {children}
      </thead>
    );
  },
  tbody: function TbodyComponent({ children, ...props }: any) {
    return <tbody {...props}>{children}</tbody>;
  },
  tr: function TrComponent({ children, ...props }: any) {
    return (
      <tr className='border-b border-gray-200 dark:border-gray-700' {...props}>
        {children}
      </tr>
    );
  },
  th: function ThComponent({ children, ...props }: any) {
    return (
      <th
        className='px-4 py-2 text-left font-semibold text-black dark:text-blac0 border-r border-gray-200 dark:border-gray-700 last:border-r-0'
        {...props}
      >
        {children}
      </th>
    );
  },
  td: function TdComponent({ children, ...props }: any) {
    return (
      <td className='px-4 py-2 text-black dark:text-black border-r border-gray-200 dark:border-gray-700 last:border-r-0' {...props}>
        {children}
      </td>
    );
  },
  // Blockquote component
  blockquote: function BlockquoteComponent({ children, ...props }: any) {
    return (
      <blockquote className='border-l-4 border-blue-500 dark:border-blue-400 pl-4 my-3 italic text-black dark:text-black bg-gray-50 dark:bg-gray-800/50 py-2 rounded-r-md' {...props}>
        {children}
      </blockquote>
    );
  },
  // Strong and emphasis
  strong: function StrongComponent({ children, ...props }: any) {
    return (
      <strong className='font-semibold text-black dark:text-black' {...props}>
        {children}
      </strong>
    );
  },
  em: function EmComponent({ children, ...props }: any) {
    return (
      <em className='italic text-black dark:text-black' {...props}>
        {children}
      </em>
    );
  },
};

const MemoizedMarkdownBlock = memo(
  function MarkdownBlock({
    content,
    components = INITIAL_COMPONENTS,
  }: {
    content: string;
    components?: Partial<Components>;
  }) {
    // Handle empty content
    if (!content.trim()) {
      return null;
    }
    
    try {
      return (
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]} 
          components={components}
          skipHtml={false}
        >
          {content}
        </ReactMarkdown>
      );
    } catch (error) {
      console.warn('Failed to render markdown block:', error);
      // Fallback to plain text
      return <div className="whitespace-pre-wrap">{content}</div>;
    }
  },
  function propsAreEqual(prevProps, nextProps) {
    return prevProps.content === nextProps.content;
  }
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

function MarkdownComponent({
  children,
  id,
  className,
  components = INITIAL_COMPONENTS,
}: MarkdownProps) {
  const generatedId = useId();
  const blockId = id ?? generatedId;
  
  // Optimize for streaming content - don't parse blocks for short content
  const shouldParseBlocks = useMemo(() => {
    return children && children.length > 200; // Only parse blocks for longer content
  }, [children]);
  
  const blocks = useMemo(() => {
    if (!shouldParseBlocks) {
      return [children || ''];
    }
    return parseMarkdownIntoBlocks(children);
  }, [children, shouldParseBlocks]);

  // Handle empty content
  if (!children?.trim()) {
    return null;
  }

  return (
    <div className={cn('text-black dark:text-black-100', className)}>
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock
          key={`${blockId}-block-${index}`}
          content={block}
          components={components}
        />
      ))}
    </div>
  );
}

const Markdown = memo(MarkdownComponent);
Markdown.displayName = 'Markdown';

export { Markdown };
